<script setup lang="ts">
import type { NoSearchFilterError } from '@@/bits/content_picker'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { extractUrl } from '@@/bits/html_parser'
import { __ } from '@@/bits/intl'
import { isUrl } from '@@/bits/url'
import OzBackButton from '@@/library/v4/components/OzBackButton.vue'
import OzCloseButton from '@@/library/v4/components/OzCloseButton.vue'
import OzIconButton, { OzIconButtonColorScheme } from '@@/library/v4/components/OzIconButton.vue'
import OzPlainButton, { OzPlainButtonColorScheme } from '@@/library/v4/components/OzPlainButton.vue'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'

const props = defineProps<{
  xBackButton: boolean
  xClipboardButton: boolean
  shouldWiggle: boolean
  shouldForceFocusOnMount: boolean
  searchBarPlaceholder: string
  searchQuery: string
  submitButtonDataId?: string
  canAcceptLink?: boolean
}>()

const emit = defineEmits<{
  (name: 'back'): void
  (name: 'blur'): void
  (name: 'search', query: string): void
  (name: 'clear-search'): void
  (name: 'clear-search-error'): void
  (name: 'click'): void
  (name: 'close'): void
  (name: 'copy'): void
  (name: 'error', error: NoSearchFilterError): void
  (name: 'focus'): void
  (name: 'input', value: string): void
  (name: 'paste-click'): void
  (name: 'select', data: string): void
}>()

// https://docs.microsoft.com/en-us/rest/api/cognitiveservices-bingsearch/bing-images-api-v5-reference
// The maximum URL length is 2,048 characters. To ensure that your URL length does not exceed the limit,
// the maximum length of your query parameters should be less than 1,500 characters.
// If the URL exceeds 2,048 characters, the server returns 404 Not found.
// Need to account for percent-encoded multi-code-point UTF-8 string.
const MAX_SEARCH_LENGTH = 1000

const { isPhone } = storeToRefs(useWindowSizeStore())

const searchBar = ref<HTMLInputElement | null>(null)

const isSearchBarFocused = ref<boolean>(false)

const shouldShowClipboardButton = computed(
  () => props.xClipboardButton && !isPhone.value && !device.safari && !props.searchQuery,
)
const shouldShowSubmitButton = computed(() =>
  isSearchBarFocused.value ? !shouldShowClipboardButton.value : props.searchQuery,
)
const isShowingRightButton = computed(() => shouldShowClipboardButton.value || shouldShowSubmitButton.value)

const handleCopyText = (): void => {
  emit('copy')
}

const handleSearchBarFocus = (): void => {
  isSearchBarFocused.value = true
  emit('focus')
}

const onInput = (event): void => {
  emit('input', event.target.value)
  emit('clear-search-error')
}

const select = (data: string): void => {
  emit('select', data)
}

const unfocusForTouchDevices = () => {
  if (device.touchable && searchBar.value) {
    // If it's touchable, keyboard will popup, we don't want that
    searchBar.value.blur()
  }
}

// If the search query is a link, use it as post attachment by calling this.select
// Else use it to search with this.search
// If we are in link input mode, default to web search
const processQuery = (): void => {
  const query = props.searchQuery
  emit('clear-search-error')
  unfocusForTouchDevices()

  if (!query) {
    emit('clear-search')
    return
  }
  if (props.canAcceptLink) {
    if (isUrl(query)) {
      select(query)
      return
    }
    if (isUrl(`https://${query}`)) {
      select(`https://${query}`)
      return
    }
    const extractedUrl = extractUrl(query)
    if (extractedUrl) {
      select(extractedUrl)
      return
    }
  }

  emit('search', query)
}

const handleSearchBarFocusOut = (): void => {
  isSearchBarFocused.value = false
  if (device.iphone) {
    processQuery()
  }
  emit('blur')
}

const resetSearch = (): void => {
  emit('clear-search')
  searchBar.value?.focus()
}

const focusSearchBar = (): void => {
  searchBar.value?.focus()
}

const handleEnter = (event): void => {
  if (!props.searchQuery) {
    event.preventDefault()
  }
}

onMounted((): void => {
  if ((!device.touchable || props.shouldForceFocusOnMount) && searchBar.value) {
    // If it's touchable, keyboard will popup, we don't want that
    searchBar.value.focus()
  }
})

defineExpose({
  focusSearchBar,
})
</script>

<template>
  <div :class="['flex', 'p-4', '@tablet-portrait/surface-container:pb-3', 'tablet-portrait:-@:pb-3', 'relative']">
    <OzBackButton
      v-if="xBackButton"
      :color-scheme="isPhone ? OzIconButtonColorScheme.Secondary : OzIconButtonColorScheme.SecondaryClear"
      @click="emit('back')"
    />
    <OzCloseButton
      v-else
      :color-scheme="isPhone ? OzIconButtonColorScheme.Secondary : OzIconButtonColorScheme.SecondaryClear"
      data-testid="contentPickerCloseButton"
      @click="emit('close')"
    />

    <div
      :class="[
        'flex',
        'ms-4',
        'flex-row',
        'items-center',
        'relative',
        'h-10 border-2 border-solid rounded-2xl',
        'bg-grey-50 dark:bg-grey-800',
        isSearchBarFocused ? 'border-grape-500 dark:border-canary-500' : 'border-transparent',
        shouldWiggle && 'animate-wiggle',
        'box-border',

        // We animate the input focus (which shows the right button) by overlaying it on top of the right button and changing the width programmatically
        'z-10',
        'transition-[width] ease-in duration-200',
        // Right icon button not visible: 56 = 40 (left button width) + 16 (margin-start for this div)
        // Right icon button visible: 112 = 56 (explained above) + 12 (margin between input and right button) + 40 (right button width)
        isShowingRightButton ? 'w-[calc(100%-112px)]' : 'w-[calc(100%-56px)]',
      ]"
    >
      <form class="flex grow" @submit.prevent="processQuery">
        <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
        <input
          ref="searchBar"
          data-testid="contentPickerSearchBar"
          :dir="dir()"
          class="search-input"
          type="text"
          autocapitalize="off"
          :maxlength="MAX_SEARCH_LENGTH"
          :placeholder="searchBarPlaceholder"
          :aria-label="__('Search')"
          :value="searchQuery"
          @focusin="handleSearchBarFocus"
          @focusout="handleSearchBarFocusOut"
          @copy="handleCopyText"
          @cut="handleCopyText"
          @click="emit('click')"
          @input="onInput"
          @keydown.enter="handleEnter"
        />
      </form>
      <OzPlainButton
        v-if="!isPhone && searchQuery"
        :class="['px-3', 'text-17', 'font-semibold']"
        :color-scheme="OzPlainButtonColorScheme.Secondary"
        :text="__('Clear')"
        @click="resetSearch"
      />
    </div>
    <div :class="['w-10', 'absolute end-4', isShowingRightButton ? 'visible' : 'invisible']">
      <!-- We show a different clipboard button as part of the menu list on mobile -->
      <OzIconButton
        v-show="shouldShowClipboardButton"
        key="desktop-right-icon-paste"
        :label="__('Paste clipboard')"
        :color-scheme="OzIconButtonColorScheme.SecondaryClear"
        icon-name="clipboard_outline"
        :icon-size="20"
        data-testid="contentPickerPasteClipboardButton"
        @click="emit('paste-click')"
      />
      <!-- We set the tabindex to -1 to prevent the submit button from being focused when it is going to be hidden,
           so that the focus doesn't disappear from the document -->
      <OzIconButton
        v-show="shouldShowSubmitButton"
        key="desktop-right-icon-forward"
        data-testid="contentPickerSubmitButton"
        :class="!searchQuery && 'aria-disabled:!bg-transparent'"
        :label="__('Submit')"
        :color-scheme="isPhone ? OzIconButtonColorScheme.Secondary : OzIconButtonColorScheme.SecondaryClear"
        icon-name="arrow_right"
        :data-pepin-id="submitButtonDataId"
        :disabled="!searchQuery"
        :tabindex="!searchQuery ? -1 : undefined"
        data-pepin-trigger="click"
        @click="processQuery"
      />
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.search-input {
  @apply my-0 ps-3 md:ps-4 pe-3;
  @apply shadow-none focus:shadow-none;
  @apply font-sans dark:text-white-100 placeholder-dark-text-200 dark:placeholder-light-text-200;
  @apply text-input-body;
  @apply md:text-input-body-small;
  @apply w-full;
  @apply border-none;
  @apply bg-transparent;
}
.skinny-button {
  :deep(.text-label) {
    margin: 0;
    margin-right: 4px;
  }
}
</style>
